import { Button } from '@base-ui/react/button';
import { Menu } from '@base-ui/react/menu';
import { Tooltip } from '@base-ui/react/tooltip';
import { faLock } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link, useNavigate } from '@tanstack/react-router';
import { type ReactNode, useState } from 'react';
import styled, { css } from 'styled-components';
import api, { queryClient } from '@/api';
import { apiErrorDetail } from '@/api/errorDetail';
import type { components } from '@/api/qraft/schema';
import buttonStyles from '~/styles/button.module.css';
import { ConfirmDialog } from './ConfirmDialog';
import { ErrorText } from './Form';

type VersionState = components['schemas']['VersionState'];
type VersionSummary = components['schemas']['CourseVersionSummary'];
type ActorRef = components['schemas']['ActorRef'];
export type ChangeEntity = components['schemas']['ChangeEntity'];
export type ChangeKind = components['schemas']['ChangeKind'];

// An item inside a course version, so the band can move between versions and land
// on the same lesson, topic or question.
export type VersionItem = { entity: ChangeEntity; lineageId: number };

export type VersionTarget = {
  courseId: number;
  version: number;
  item?: VersionItem;
};

type Deletable = {
  id: number;
  lineage_id?: number | null;
  version_state?: VersionState;
};

// A draft's copy of a published item comes back from Review changes; anything
// else is gone for good.
export function deletePrompt(noun: string, item: Deletable): string {
  const restorable =
    item.version_state === 'draft' &&
    item.lineage_id != null &&
    item.lineage_id !== item.id;
  return restorable
    ? `Delete this ${noun}? You can restore it from Review changes until this draft is published.`
    : `Delete this ${noun}? This cannot be undone.`;
}

// A typed place, kept between the first and the last there is.
export function clampPosition(value: string, last: number): string {
  const n = Math.round(Number(value));
  return String(Number.isFinite(n) ? Math.min(Math.max(n, 1), last) : 1);
}

// Times read as "4:05 pm", never 16:05.
export function formatWhen(iso: string): string {
  const date = new Date(iso);
  const time = date
    .toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    .toLowerCase();
  const today = new Date();
  if (date.toDateString() === today.toDateString()) return `today at ${time}`;
  const day = date.toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    year: date.getFullYear() === today.getFullYear() ? undefined : 'numeric',
  });
  return `${day} at ${time}`;
}

function byWhom(actor: ActorRef | null | undefined): string {
  return actor?.name ? ` by ${actor.name}` : '';
}

export function useCourseVersions(courseId: number | null) {
  return api.adminListCourseVersions.useQuery(
    { path: { course_id: courseId ?? 0 } },
    { enabled: courseId !== null },
  );
}

// Everything a version change touches: its version list, its pages, the lists.
export function invalidateCourseVersions(courseId: number) {
  queryClient.invalidateQueries({
    queryKey: api.adminListCourseVersions.getQueryKey({
      path: { course_id: courseId },
    }),
  });
  queryClient.invalidateQueries({
    queryKey: api.adminGetCourse.getQueryKey({ path: { course_id: courseId } }),
  });
  queryClient.invalidateQueries({
    queryKey: api.adminGetCourseOutline.getQueryKey({
      path: { course_id: courseId },
    }),
  });
  queryClient.invalidateQueries({
    queryKey: api.adminListCourses.getQueryKey({}),
  });
}

const STATE_LABEL: Record<VersionState, string> = {
  live: 'Live',
  draft: 'Draft',
  earlier: 'Earlier',
};

export function VersionPill({
  state,
  version,
  detail,
}: {
  state: VersionState;
  version: number;
  // Who and when, on hover: a list stays scannable without them.
  detail?: string;
}) {
  const label = `${STATE_LABEL[state]} v${version}`;
  if (!detail) return <Pill $state={state}>{label}</Pill>;
  return (
    <Tooltip.Root>
      <Tooltip.Trigger
        render={
          <Pill
            $state={state}
            tabIndex={0}
            aria-label={`${label}, ${detail}`}
          />
        }
      >
        {label}
      </Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Positioner sideOffset={6}>
          <TipPopup>{detail}</TipPopup>
        </Tooltip.Positioner>
      </Tooltip.Portal>
    </Tooltip.Root>
  );
}

// Where the same item lives in another version: its page, or the course page when
// that version doesn't have it.
export function useOpenInVersion() {
  const navigate = useNavigate();
  return async (target: VersionTarget) => {
    const course = () =>
      navigate({
        to: '/courses/$courseId/$version',
        params: {
          courseId: String(target.courseId),
          version: String(target.version),
        },
      });
    if (!target.item) return course();
    try {
      const found = await api.adminFindInCourseVersion.fetchQuery({
        parameters: {
          path: { course_id: target.courseId, version: target.version },
          query: {
            entity: target.item.entity,
            lineage_id: target.item.lineageId,
          },
        },
      });
      const id = String(found.id);
      if (target.item.entity === 'lesson')
        return navigate({ to: '/lessons/$lessonId', params: { lessonId: id } });
      if (target.item.entity === 'topic')
        return navigate({ to: '/topics/$topicId', params: { topicId: id } });
      if (target.item.entity === 'question')
        return navigate({
          to: '/questions/$questionId',
          params: { questionId: id },
        });
      return course();
    } catch {
      return course();
    }
  };
}

/**
 * The strip above a draft's pages. Only drafts get one: editing a draft must never
 * be mistaken for editing live. Live and earlier versions show just the pill menu.
 */
export function VersionBand({
  courseId,
  version,
}: {
  courseId: number;
  version: number;
}) {
  const { data: versions } = useCourseVersions(courseId);
  const current = versions?.find((v) => v.version === version);
  const live = versions?.find((v) => v.state === 'live');
  // A course that was never published has nothing live to protect: it's edited
  // directly, as before.
  if (!current || !live || current.state !== 'draft') return null;
  return (
    <Band aria-label="Draft version">
      <BandText>
        <strong>Draft v{version}</strong> · Students can't see this · Started
        {byWhom(current.created_by)} {formatWhen(current.created_at)}
        {current.last_changed_by && (
          <>
            {' '}
            · Last change{byWhom(current.last_changed_by)}{' '}
            {formatWhen(current.last_changed_at)}
          </>
        )}
      </BandText>
      <BandActions>
        <PreviewButton courseId={courseId} version={version} />
        <Link
          className={buttonStyles.Button}
          to="/courses/$courseId/$version/changes"
          params={{ courseId: String(courseId), version: String(version) }}
        >
          Review changes
        </Link>
      </BandActions>
    </Band>
  );
}

// Opens a version in the student web app, in a new tab. The tab is opened on the
// click itself, before the link is minted, or the browser would block it as a popup.
function usePreview(courseId: number, version: number) {
  const [failed, setFailed] = useState(false);
  const mint = api.adminPreviewCourseVersion.useMutation(undefined, {
    meta: { toast: false },
  });
  const open = async () => {
    setFailed(false);
    const tab = window.open('', '_blank');
    // The preview renders draft HTML; it mustn't be able to steer this tab.
    if (tab) tab.opener = null;
    try {
      const link = await mint.mutateAsync({
        path: { course_id: courseId, version },
      });
      if (tab) tab.location.href = link.url;
      else window.open(link.url, '_blank', 'noopener');
    } catch {
      tab?.close();
      setFailed(true);
    }
  };
  return { open, pending: mint.isPending, failed };
}

function PreviewButton({
  courseId,
  version,
}: {
  courseId: number;
  version: number;
}) {
  const preview = usePreview(courseId, version);
  return (
    <>
      <Button
        className={buttonStyles.Button}
        disabled={preview.pending}
        onClick={() => void preview.open()}
      >
        Preview as student ↗
      </Button>
      {preview.failed && <ErrorText>Could not open the preview.</ErrorText>}
    </>
  );
}

function studentsOn(n: number): string {
  return `${n} ${n === 1 ? 'student' : 'students'}`;
}

/**
 * The version pill beside a page title, and the menu behind it: every version of
 * the course, and what to do next. Everything about versions is reachable here
 * without taking over pages where nobody is working on one.
 */
export function VersionMenu({
  courseId,
  version,
  item,
}: {
  courseId: number;
  version: number;
  item?: VersionItem;
}) {
  const { data: versions } = useCourseVersions(courseId);
  const [starting, setStarting] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const open = useOpenInVersion();
  const navigate = useNavigate();
  const preview = usePreview(courseId, version);

  const current = versions?.find((v) => v.version === version);
  const live = versions?.find((v) => v.state === 'live');
  const draft = versions?.find((v) => v.state === 'draft');
  if (!versions || !current || !live) return null;

  // Earlier versions nobody is on any more fold under "Older versions".
  const active = versions.filter(
    (v) => v.state !== 'earlier' || v.in_progress > 0 || v.version === version,
  );
  const older = versions.filter((v) => !active.includes(v));
  const entry = (v: VersionSummary) => (
    <MenuItem
      key={v.version}
      onClick={() => open({ courseId, version: v.version, item })}
    >
      <Check aria-hidden>{v.version === version ? '✓' : ''}</Check>
      <span>
        {STATE_LABEL[v.state]} v{v.version}
      </span>
      <Hint>
        {v.state === 'draft'
          ? byWhom(v.created_by).replace(' by ', '')
          : `${studentsOn(v.in_progress)} in progress`}
      </Hint>
    </MenuItem>
  );

  return (
    <>
      <Menu.Root>
        <PillTrigger $state={current.state}>
          {STATE_LABEL[current.state]} v{version} <span aria-hidden>▾</span>
        </PillTrigger>
        <Menu.Portal>
          <Menu.Positioner sideOffset={6} align="start">
            <MenuPopup>
              <Menu.Group>
                <MenuLabel>Versions</MenuLabel>
                {active.map(entry)}
              </Menu.Group>
              {/* A long history stays one row: its own scrolling submenu. */}
              {older.length > 0 && (
                <Menu.SubmenuRoot>
                  <SubmenuTrigger>
                    <Check />
                    Older versions
                    <Hint>
                      {older.length} <span aria-hidden>▸</span>
                    </Hint>
                  </SubmenuTrigger>
                  <Menu.Portal>
                    <Menu.Positioner sideOffset={4} align="start">
                      <MenuPopup>{older.map(entry)}</MenuPopup>
                    </Menu.Positioner>
                  </Menu.Portal>
                </Menu.SubmenuRoot>
              )}
              <MenuSeparator />
              <MenuItem onClick={() => void preview.open()}>
                <Check />
                Preview as student ↗
              </MenuItem>
              {current.state === 'draft' ? (
                <>
                  <MenuItem
                    onClick={() =>
                      navigate({
                        to: '/courses/$courseId/$version/changes',
                        params: {
                          courseId: String(courseId),
                          version: String(version),
                        },
                      })
                    }
                  >
                    <Check />
                    Review changes
                  </MenuItem>
                  <MenuItem $danger onClick={() => setDiscarding(true)}>
                    <Check />
                    Discard draft…
                  </MenuItem>
                </>
              ) : draft ? (
                <MenuItem
                  onClick={() =>
                    open({ courseId, version: draft.version, item })
                  }
                >
                  <Check />
                  Go to draft v{draft.version}
                </MenuItem>
              ) : (
                <MenuItem $accent onClick={() => setStarting(true)}>
                  <Plus aria-hidden>+</Plus>
                  {current.state === 'live'
                    ? 'Start a new version'
                    : `Start a new version from v${version}`}
                </MenuItem>
              )}
            </MenuPopup>
          </Menu.Positioner>
        </Menu.Portal>
      </Menu.Root>
      {preview.failed && <ErrorText>Could not open the preview.</ErrorText>}
      <StartVersionDialog
        open={starting}
        courseId={courseId}
        versions={versions}
        defaultSource={version}
        item={item}
        onClose={() => setStarting(false)}
      />
      <DiscardDraftDialog
        open={discarding}
        courseId={courseId}
        version={version}
        liveVersion={live.version}
        onClose={() => setDiscarding(false)}
      />
    </>
  );
}

export function StartVersionDialog({
  open,
  courseId,
  versions,
  defaultSource,
  item,
  onClose,
}: {
  open: boolean;
  courseId: number;
  versions: VersionSummary[];
  defaultSource: number;
  item?: VersionItem;
  onClose: () => void;
}) {
  const sources = versions.filter((v) => v.state !== 'draft');
  const [source, setSource] = useState(defaultSource);
  const openIn = useOpenInVersion();
  const next = Math.max(...versions.map((v) => v.version)) + 1;
  const title = versions[0]?.title ?? 'this course';
  const start = api.adminStartCourseVersion.useMutation(undefined, {
    meta: { toast: 'Draft started' },
    onSuccess: (created) => {
      invalidateCourseVersions(courseId);
      onClose();
      openIn({ courseId, version: created.version, item });
    },
    onError: (error) => {
      // Someone else started one a moment ago: go to theirs.
      const data = (
        error as { data?: { error_code?: string; version?: number } }
      ).data;
      if (data?.error_code === 'draft_exists' && data.version) {
        invalidateCourseVersions(courseId);
        onClose();
        openIn({ courseId, version: data.version, item });
      }
    },
  });
  return (
    <ConfirmDialog
      open={open}
      title={`Start version ${next} of ${title}?`}
      confirmLabel={`Start draft v${next}`}
      tone="primary"
      pending={start.isPending}
      onConfirm={() =>
        start.mutate({
          path: { course_id: courseId },
          body: { source_version: source },
        })
      }
      onCancel={onClose}
    >
      <p>
        We'll copy every lesson, topic, question and answer into a draft you can
        change freely. Students keep seeing the live version until you publish.
      </p>
      <SourceList role="radiogroup" aria-label="Copy from">
        {sources.map((v) => (
          <SourceOption key={v.version} $selected={v.version === source}>
            <input
              type="radio"
              name="start-version-source"
              value={v.version}
              checked={v.version === source}
              onChange={() => setSource(v.version)}
            />
            <span>
              <strong>
                Copy {STATE_LABEL[v.state].toLowerCase()} v{v.version}
              </strong>
              <small>
                {v.state === 'earlier'
                  ? 'Use this to bring back an older version'
                  : 'The version new students start'}
                {' · '}
                {studentsOn(v.in_progress)} in progress
              </small>
            </span>
          </SourceOption>
        ))}
      </SourceList>
      {start.isError && (
        <ErrorText>
          {apiErrorDetail(start.error) ??
            'Could not start a new version. Please try again.'}
        </ErrorText>
      )}
    </ConfirmDialog>
  );
}

function DiscardDraftDialog({
  open,
  courseId,
  version,
  liveVersion,
  onClose,
}: {
  open: boolean;
  courseId: number;
  version: number;
  liveVersion: number;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const discard = api.adminDiscardCourseVersion.useMutation(undefined, {
    meta: { toast: 'Draft discarded' },
    onSuccess: () => {
      invalidateCourseVersions(courseId);
      onClose();
      navigate({
        to: '/courses/$courseId/$version',
        params: { courseId: String(courseId), version: String(liveVersion) },
      });
    },
  });
  return (
    <ConfirmDialog
      open={open}
      title={`Discard draft v${version}?`}
      confirmLabel="Discard draft"
      tone="danger"
      pending={discard.isPending}
      onConfirm={() =>
        discard.mutate({ path: { course_id: courseId, version } })
      }
      onCancel={onClose}
    >
      <p>
        Every change in this draft is deleted. The live version v{liveVersion}{' '}
        stays exactly as it is.
      </p>
      {discard.isError && (
        <ErrorText>
          {apiErrorDetail(discard.error) ??
            'Could not discard the draft. Please try again.'}
        </ErrorText>
      )}
    </ConfirmDialog>
  );
}

/**
 * In place of a control that would change a published version's structure:
 * says why, and offers the draft (or starts one) at the same item.
 */
export function NeedsNewVersion({
  target,
  children,
}: {
  target: VersionTarget;
  children: ReactNode;
}) {
  const { data: versions } = useCourseVersions(target.courseId);
  const [starting, setStarting] = useState(false);
  const open = useOpenInVersion();
  if (!versions) return null;
  const draft = versions.find((v) => v.state === 'draft');
  return (
    <Callout role="note">
      <FontAwesomeIcon icon={faLock} aria-hidden />
      <p>
        {children}{' '}
        {draft
          ? `Make the change in draft v${draft.version}.`
          : 'Make the change in a new version; students on this one keep it as it is.'}
      </p>
      {draft ? (
        <TextButton
          onClick={() =>
            open({
              courseId: target.courseId,
              version: draft.version,
              item: target.item,
            })
          }
        >
          Open in draft v{draft.version} →
        </TextButton>
      ) : (
        <TextButton onClick={() => setStarting(true)}>
          Start a new version →
        </TextButton>
      )}
      <StartVersionDialog
        open={starting}
        courseId={target.courseId}
        versions={versions}
        defaultSource={target.version}
        item={target.item}
        onClose={() => setStarting(false)}
      />
    </Callout>
  );
}

// "Students see this immediately", beside wording edits on a published version.
export function LiveEditNotice({ state }: { state: VersionState | undefined }) {
  if (state !== 'live' && state !== 'earlier') return null;
  return (
    <LiveNotice>
      Editing a published version: students see wording and image changes
      immediately. Structure changes go in a new version.
    </LiveNotice>
  );
}

type EditConflictData = {
  error_code: 'edit_conflict';
  updated_at: string;
  updated_by?: ActorRef | null;
};

export function editConflict(error: unknown): EditConflictData | null {
  const data = (error as { data?: { error_code?: string } } | null)?.data;
  return data?.error_code === 'edit_conflict'
    ? (data as EditConflictData)
    : null;
}

// Someone else saved first: say who and when, instead of overwriting them.
export function EditConflictNotice({
  error,
  onReload,
}: {
  error: unknown;
  onReload: () => void;
}) {
  const conflict = editConflict(error);
  if (!conflict) return null;
  return (
    <ConflictBox role="alert">
      <p>
        <strong>
          {conflict.updated_by?.name ?? 'Someone else'} changed this{' '}
          {formatWhen(conflict.updated_at)}, after you opened it.
        </strong>{' '}
        Saving now would replace their change. Copy anything you want to keep,
        then reload to see it.
      </p>
      <Button className={buttonStyles.Button} onClick={onReload}>
        Reload
      </Button>
    </ConflictBox>
  );
}

const MARK: Record<ChangeKind, { glyph: string; label: string }> = {
  added: { glyph: '+', label: 'Added' },
  edited: { glyph: '●', label: 'Edited' },
  moved: { glyph: '↕', label: 'Moved' },
  removed: { glyph: '−', label: 'Removed' },
};

export function ChangeMark({ kind }: { kind: ChangeKind }) {
  return (
    <Mark $kind={kind} title={MARK[kind].label} aria-label={MARK[kind].label}>
      {MARK[kind].glyph}
    </Mark>
  );
}

const TONE: Record<VersionState, string> = {
  live: 'var(--success)',
  draft: 'var(--orange)',
  earlier: 'var(--grey-blue-300)',
};

const KIND_TONE: Record<ChangeKind, string> = {
  added: 'var(--success)',
  edited: 'var(--orange)',
  moved: 'var(--blue)',
  removed: 'var(--error)',
};

const Band = styled.section`
  align-items: center;
  background: color-mix(in srgb, var(--orange) 10%, transparent);
  border: 1px dashed var(--orange);
  border-radius: 0.375rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem 1rem;
  justify-content: space-between;
  margin-bottom: 0.75rem;
  padding: 0.3125rem 0.625rem;
`;

const BandActions = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const BandText = styled.p`
  color: var(--grey-900);
  font-size: 0.8125rem;
  margin: 0;

  strong {
    color: var(--grey-1000);
  }
`;

// A version's pill, shared by the plain tag and the menu trigger.
const pillStyles = css<{ $state: VersionState }>`
  background: color-mix(in srgb, ${(p) => TONE[p.$state]} 14%, transparent);
  border: 1px ${(p) => (p.$state === 'draft' ? 'dashed' : 'solid')}
    ${(p) => TONE[p.$state]};
  border-radius: 999px;
  color: var(--grey-1000);
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  padding: 0.125rem 0.5rem;
  text-transform: uppercase;
  vertical-align: middle;
  white-space: nowrap;
`;

const PillTrigger = styled(Menu.Trigger)<{ $state: VersionState }>`
  ${pillStyles}
  cursor: pointer;
  font-family: inherit;
  line-height: inherit;

  &:hover {
    background: color-mix(in srgb, ${(p) => TONE[p.$state]} 24%, transparent);
  }
`;

const MenuPopup = styled(Menu.Popup)`
  background: var(--component-color);
  border: 1px solid var(--grey-200);
  border-radius: 0.5rem;
  box-shadow: var(--shadow-popover);
  font-size: 0.875rem;
  max-height: min(var(--available-height), 24rem);
  min-width: 16rem;
  outline: none;
  overflow-y: auto;
  padding: 0.25rem;
  z-index: 50;
`;

const MenuLabel = styled(Menu.GroupLabel)`
  color: var(--grey-700);
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  padding: 0.375rem 0.625rem 0.125rem;
  text-transform: uppercase;
`;

const itemStyles = css<{ $danger?: boolean; $accent?: boolean }>`
  align-items: baseline;
  border-radius: 0.25rem;
  color: ${(p) =>
    p.$danger
      ? 'var(--error)'
      : p.$accent
        ? 'var(--blue)'
        : 'var(--grey-1000)'};
  cursor: pointer;
  display: flex;
  font-weight: ${(p) => (p.$accent ? 600 : 'inherit')};
  gap: 0.375rem;
  outline: none;
  padding: 0.375rem 0.625rem;

  &[data-highlighted],
  &[data-popup-open] {
    background: var(--grey-100);
  }
`;

const MenuItem = styled(Menu.Item)<{ $danger?: boolean; $accent?: boolean }>`
  ${itemStyles}
`;

const SubmenuTrigger = styled(Menu.SubmenuTrigger)<{
  $danger?: boolean;
  $accent?: boolean;
}>`
  ${itemStyles}
`;

const Check = styled.span`
  color: var(--success);
  flex: none;
  width: 1rem;
`;

// The start action's marker takes its accent colour, not the green of the ticks.
const Plus = styled(Check)`
  color: inherit;
`;

const Hint = styled.span`
  color: var(--grey-700);
  font-size: 0.8125rem;
  margin-left: auto;
  padding-left: 1rem;
`;

const MenuSeparator = styled(Menu.Separator)`
  border-top: 1px solid var(--grey-200);
  margin: 0.25rem 0;
`;

const Pill = styled.span<{ $state: VersionState }>`
  ${pillStyles}
`;

const TipPopup = styled(Tooltip.Popup)`
  background: var(--grey-1000);
  border-radius: 0.375rem;
  box-shadow: var(--shadow-popover);
  color: var(--component-color);
  font-size: 0.8125rem;
  padding: 0.375rem 0.625rem;
  z-index: 50;
`;

const SourceList = styled.div`
  display: grid;
  gap: 0.5rem;
  margin-top: 0.75rem;
`;

const SourceOption = styled.label<{ $selected: boolean }>`
  align-items: flex-start;
  border: 1px solid
    ${(p) => (p.$selected ? 'var(--grey-700)' : 'var(--grey-300)')};
  border-radius: 0.375rem;
  cursor: pointer;
  display: flex;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;

  span {
    display: grid;
  }

  small {
    color: var(--grey-700);
  }
`;

// A quiet note where a locked control would be: the page's own actions keep the
// emphasis, and the way forward is a text action rather than a primary button.
const Callout = styled.div`
  align-items: baseline;
  background: var(--grey-50);
  border: 1px solid var(--grey-200);
  border-radius: 0.375rem;
  color: var(--grey-700);
  column-gap: 0.625rem;
  display: flex;
  flex-wrap: wrap;
  font-size: 0.875rem;
  margin-bottom: var(--component-margin);
  padding: 0.5rem 0.75rem;
  row-gap: 0.25rem;

  svg {
    color: var(--grey-500);
    font-size: 0.75rem;
  }

  p {
    color: var(--grey-800);
    flex: 1 1 18rem;
    line-height: 1.45;
    margin: 0;
  }
`;

const TextButton = styled(Button)`
  background: none;
  border: 0;
  color: var(--blue);
  cursor: pointer;
  font: inherit;
  font-weight: 600;
  padding: 0;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }
`;

const LiveNotice = styled.p`
  background: color-mix(in srgb, var(--success) 10%, transparent);
  border-left: 3px solid var(--success);
  color: var(--grey-900);
  font-size: 0.8125rem;
  margin: 0 0 0.5rem;
  padding: 0.5rem 0.75rem;
`;

const ConflictBox = styled.div`
  align-items: center;
  background: color-mix(in srgb, var(--error) 8%, transparent);
  border-left: 3px solid var(--error);
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: space-between;
  padding: 0.5rem 0.75rem;

  p {
    color: var(--grey-900);
    font-size: 0.8125rem;
    margin: 0;
  }
`;

const Mark = styled.span<{ $kind: ChangeKind }>`
  background: color-mix(in srgb, ${(p) => KIND_TONE[p.$kind]} 16%, transparent);
  border-radius: 0.1875rem;
  color: ${(p) => KIND_TONE[p.$kind]};
  display: inline-grid;
  flex: none;
  font-size: 0.6875rem;
  font-weight: 700;
  height: 1rem;
  line-height: 1;
  place-items: center;
  width: 1rem;
`;
